import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import LayoutContainer from 'components/Common/LayoutContainer'
import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { removeOfferComparisonSelection, clearAllOfferComparisonSelections } from 'actions/OfferComparisonActions'
import { selectOfferComparisonEnabled, selectOfferComparisonSelections } from 'selectors/offerComparisonSelectors'
import zIndex from 'styles/tools/z-index'
import OfferComparisonModal from './OfferComparisonModal'
import OfferFooterChip from './OfferFooterChip'
import { MAX_COMPARISON_SELECTIONS } from 'constants/offer'
import { trackClientEvent } from 'analytics/analytics'

const Root = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};
  box-shadow: ${props => props.theme.shadow.top.medium};
  z-index: ${zIndex.stickyFooter};
`

const ChipsSection = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`

const AddAnother = styled(AssistChip)`
  &.variant-filled:disabled {
    outline: 1px dashed ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    outline-offset: -1px;
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    box-shadow: none;
    cursor: default;
  }
`

function OfferSelectionStickyFooter() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const enabled = useAppSelector(selectOfferComparisonEnabled)
  const selections = useAppSelector(selectOfferComparisonSelections)
  const canAddSelection = selections.length < MAX_COMPARISON_SELECTIONS

  const showModal = useContext(ModalContext)

  const deleteSelection = useCallback((selectionId: string) => {
    dispatch(removeOfferComparisonSelection(selectionId))
  }, [dispatch])

  const clearAll = useCallback(() => {
    dispatch(clearAllOfferComparisonSelections())
  }, [dispatch])

  const openComparisonModal = useCallback(() => {
    trackClientEvent({
      subject: 'search_page_offer_comparison',
      action: `click:${selections.length}`,
      type: 'interaction',
      category: 'search page',
    })
    showModal(<OfferComparisonModal />)
  }, [showModal, selections.length])

  const selectAnotherOfferLabel = intl.formatMessage({
    id: 'xDHg2UDYU2QDzDaI_EGS',
    defaultMessage: 'Select another offer',
    description: 'Chip text prompting user to select another offer for comparison when one is selected',
  })

  const selectOneMoreLabel = intl.formatMessage({
    id: 'Owl8wWygH2ym_KnoJEC8',
    defaultMessage: 'Select one more?',
    description: 'Chip text prompting user to select one more offer for comparison when two or more are selected',
  })

  const clearAllLabel = intl.formatMessage({
    id: 'jx0m64T1-cXonXsj4skq',
    defaultMessage: 'Clear all',
    description: 'Button text to clear all selected offers from comparison',
  })

  const compareOffersLabel = intl.formatMessage({
    id: 'TgpNUHLIzLgHLnEeNOcQ',
    defaultMessage: 'Compare offers',
    description: 'Button text to open comparison modal for selected offers',
  })

  const compareCountOffersLabel = intl.formatMessage(
    {
      id: 'eKWrHxVhCzBIVJiOQxJO',
      defaultMessage: 'Compare {count} offers',
      description: 'Button text to open comparison modal showing count of selected offers',
    },
    { count: selections.length },
  )

  if (!enabled || selections.length === 0) {
    return null
  }

  return <Root>
    <LayoutContainer size="xl">
      <Group direction="vertical" gap={16} desktopDirection="horizontal" desktopHorizontalAlign="space-between">
        <ChipsSection>
          <HorizontalContainerWithFadedOverflow gap={8}>
            {selections.map(selection => (
              <OfferFooterChip key={selection.selectionId} selection={selection} onRemove={deleteSelection} />
            ))}

            {canAddSelection && <AddAnother
              variant="filled"
              size="medium"
              disabled
            >
              <BodyText variant="medium">
                {selections.length === 1 && <>{selectAnotherOfferLabel}</>}
                {selections.length > 1 && <>{selectOneMoreLabel}</>}
              </BodyText>
            </AddAnother>}
          </HorizontalContainerWithFadedOverflow>
        </ChipsSection>

        <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
          <TextButton kind="tertiary" onClick={clearAll}>{clearAllLabel}</TextButton>
          <TextButton kind="primary" onClick={openComparisonModal}>
            {selections.length < 3 ? compareOffersLabel : compareCountOffersLabel}
          </TextButton>
        </Group>
      </Group>
    </LayoutContainer>
  </Root>
}

export default OfferSelectionStickyFooter
